import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { ContactWithRelations } from "@/types/contact.types";
import { useContacts } from "@/hooks/useContacts";

interface MergeContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  contact: ContactWithRelations;
  onMerge: (sourceId: string, targetId: string) => Promise<void>;
}

export function MergeContactDialog({
  isOpen,
  onClose,
  contact,
  onMerge,
}: MergeContactDialogProps) {
  const [isMerging, setIsMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string>("");
  const { contacts, isLoading } = useContacts();

  // Reset the form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTargetId("");
      setError(null);
    }
  }, [isOpen]);

  const handleMerge = async () => {
    if (!targetId) {
      setError("Please select a target contact");
      return;
    }

    try {
      setIsMerging(true);
      setError(null);
      await onMerge(contact.id, targetId);
      onClose();
    } catch (err: any) {
      setError(err.message || "An error occurred while merging the contacts");
    } finally {
      setIsMerging(false);
    }
  };

  const contactFullName = `${contact.first_name} ${contact.last_name}`;

  // Filter out the current contact from the list
  const availableContacts = contacts.filter(
    (c) => c.id !== contact.id
  );

  // Sort contacts by name
  availableContacts.sort((a, b) => {
    const aName = `${a.last_name}, ${a.first_name}`;
    const bName = `${b.last_name}, ${b.first_name}`;
    return aName.localeCompare(bName);
  });

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Merge {contactFullName}</AlertDialogTitle>
          <AlertDialogDescription>
            Select the contact to merge this one into. All data from{" "}
            <strong>{contactFullName}</strong> will be transferred to the selected
            contact, and then <strong>{contactFullName}</strong> will be
            deleted.
            <div className="mt-4">
              <p className="mb-2">Target Contact:</p>
              <Select
                disabled={isMerging || isLoading}
                value={targetId}
                onValueChange={setTargetId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a contact" />
                </SelectTrigger>
                <SelectContent>
                  {isLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading contacts...
                    </SelectItem>
                  ) : availableContacts.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No other contacts available
                    </SelectItem>
                  ) : (
                    availableContacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.last_name}, {c.first_name} {c.organization ? `(${c.organization.name})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            {error && (
              <div className="mt-2 rounded-md bg-destructive/10 p-3 text-destructive">
                {error}
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isMerging}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleMerge}
            disabled={isMerging || !targetId}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isMerging ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Merging...
              </>
            ) : (
              "Merge"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}